/**
 * Separates files into two arrays based on their size.
 *
 * @param {FileList} files - The list of files to be separated.
 * @param {number} [maxSize=10] - The maximum size (in MB) for a file to be considered small. Files larger than this size will be considered large. Default is 10 MB.
 * @returns {{ largeFiles: File[], smallFiles: File[] }} An object containing two arrays:
 *          - `largeFiles`: Files larger than `maxSize` MB.
 *          - `smallFiles`: Files smaller than or equal to `maxSize` MB.
 *
 * @example
 * const inputElement = document.querySelector('input[type="file"]');
 * inputElement?.addEventListener('change', (event) => {
 *   const files = (event.target as HTMLInputElement).files;
 *   if (files) {
 *     const { largeFiles, smallFiles } = separateFilesBySize(files);
 *     console.log('Large files:', largeFiles);
 *     console.log('Small files:', smallFiles);
 *   }
 * });
 */
export function separateFilesBySize(
  files: FileList,
  maxSize = 10
): {
  largeFiles: File[];
  smallFiles: File[];
} {
  const filesArray: File[] = Array.from(files);

  const largeFiles: File[] = [];
  const smallFiles: File[] = [];

  filesArray.forEach((file) => {
    if (file.size > maxSize * 1024 * 1024) {
      // size in bytes
      largeFiles.push(file);
    } else {
      smallFiles.push(file);
    }
  });

  return { largeFiles, smallFiles };
}
